import {
    useState,
    useRef,
    useEffect,
    createContext,
    useContext,
    ReactNode,
    HTMLAttributes,
    ButtonHTMLAttributes,
} from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useFloatingPosition } from '../../Hooks/useFloatingPosition';

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------
interface DropdownContextValue {
    open: boolean;
    setOpen: (open: boolean) => void;
    coords: { top?: number; bottom?: number; left: number; width: number; isFlipped?: boolean } | null;
    triggerRef: React.RefObject<HTMLButtonElement>;
    dropdownRef: React.RefObject<HTMLDivElement>;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);
const useDropdown = () => {
    const ctx = useContext(DropdownContext);
    if (!ctx) throw new Error('DropdownMenu subcomponents must be inside <DropdownMenu>');
    return ctx;
};

// ---------------------------------------------------------------------------
// Root
// ---------------------------------------------------------------------------
interface DropdownMenuProps {
    children: ReactNode;
    className?: string;
}

export const DropdownMenu = ({ children, className }: DropdownMenuProps) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const { coords } = useFloatingPosition(triggerRef, dropdownRef, open, { maxHeight: 300, offset: 6 });

    // Close on outside click
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    // Close on Escape
    useEffect(() => {
        const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, []);

    return (
        <DropdownContext.Provider value={{ open, setOpen, coords, triggerRef, dropdownRef }}>
            <div ref={ref} className={cn('relative inline-block', className)}>
                {children}
            </div>
        </DropdownContext.Provider>
    );
};

// ---------------------------------------------------------------------------
// Trigger
// ---------------------------------------------------------------------------
interface DropdownTriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    showCaret?: boolean;
}

export const DropdownTrigger = ({ showCaret = false, className, children, ...rest }: DropdownTriggerProps) => {
    const { open, setOpen, triggerRef } = useDropdown();
    return (
        <button
            ref={triggerRef}
            type="button"
            aria-expanded={open}
            aria-haspopup="menu"
            onClick={() => setOpen(!open)}
            className={cn(
                'inline-flex items-center gap-1.5 font-bold text-sm text-slate-600',
                'px-3 py-2 rounded-xl hover:bg-slate-100 transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500',
                className
            )}
            {...rest}
        >
            {children}
            {showCaret && (
                <ChevronDown
                    size={14}
                    className={cn('transition-transform duration-200', open && 'rotate-180')}
                />
            )}
        </button>
    );
};

// ---------------------------------------------------------------------------
// Content (the popover panel)
// ---------------------------------------------------------------------------
interface DropdownContentProps extends HTMLAttributes<HTMLDivElement> {
    align?: 'left' | 'right' | 'center';
}

export const DropdownContent = ({ align = 'right', className, children, ...rest }: DropdownContentProps) => {
    const { open, coords, dropdownRef } = useDropdown();

    if (!open || !coords) return null;

    return createPortal(
        <div
            ref={dropdownRef}
            role="menu"
            className={cn(
                'fixed z-[30000] min-w-[200px] backdrop-blur-xl bg-white/80 border border-white/50 shadow-2xl shadow-black/10 rounded-2xl',
                'py-1.5 animate-in fade-in duration-150',
                coords.isFlipped ? 'slide-in-from-bottom-2' : 'slide-in-from-top-2',
                className
            )}
            style={{
                top: coords.isFlipped ? undefined : (coords.top || 0) + 6,
                bottom: coords.isFlipped ? (coords.bottom || 0) + 6 : undefined,
                ...(align === 'left' ? { left: coords.left } : {}),
                ...(align === 'right' ? { left: coords.left + coords.width, transform: 'translateX(-100%)' } : {}),
                ...(align === 'center' ? { left: coords.left + coords.width / 2, transform: 'translateX(-50%)' } : {}),
            }}
            {...rest}
        >
            {children}
        </div>,
        document.body
    );
};

// ---------------------------------------------------------------------------
// Item
// ---------------------------------------------------------------------------
interface DropdownItemProps extends ButtonHTMLAttributes<HTMLButtonElement> {
    icon?: ReactNode;
    destructive?: boolean;
}

export const DropdownItem = ({ icon, destructive = false, className, children, onClick, ...rest }: DropdownItemProps) => {
    const { setOpen } = useDropdown();

    return (
        <button
            role="menuitem"
            type="button"
            onClick={(e) => {
                onClick?.(e);
                setOpen(false);
            }}
            className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-left',
                'transition-colors duration-100 rounded-xl mx-1',
                'focus-visible:outline-none focus-visible:bg-slate-100',
                destructive
                    ? 'text-red-500 hover:bg-red-50'
                    : 'text-slate-700 hover:bg-slate-100',
                className
            )}
            {...rest}
        >
            {icon && <span className="shrink-0">{icon}</span>}
            {children}
        </button>
    );
};

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------
export const DropdownSeparator = ({ className }: { className?: string }) => (
    <hr className={cn('my-1.5 border-0 border-t border-slate-100', className)} />
);
